import {
  ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { of } from 'rxjs';
import { filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

import {
  CompleteActiveFollowUpsGQL, EntityCoreFragment, EntityWithBasicInfoFragment, EntityWithFollowUpDetailsFragment, GetEntityById2GQL
} from 'generated/graphql';

import { WithChangesSubject } from 'core/mixins/with-changes-subject';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { ToastrService } from 'core/toastr.service';
import { FollowUpFlagDialog } from 'entity/dialogs/follow-up-flag/follow-up-flag.dialog';
import { FollowUpTableDialog } from 'entity/dialogs/follow-up-table/follow-up-table.dialog';
import { ConfirmDialogComponent } from 'shared/components/confirm-dialog/confirm-dialog-component';
import { isPast, parse } from 'date-fns';

@Component({
  selector: 'app-entity-follow-up-flag',
  templateUrl: './entity-follow-up-flag.component.html'
})
export class EntityFollowUpFlagComponent extends WithDisposableSubject(WithChangesSubject(class { })) implements OnInit, OnChanges {
  entity: EntityWithBasicInfoFragment & EntityCoreFragment & EntityWithFollowUpDetailsFragment;

  /** If corner flag is true, the corner flag template will be used */

  @Input() cornerFlag: boolean;
  @Input() entityId: string;
  @Input() campaignId: string;
  @Input() showText;

  private isSubmitting = false;

  get dueDateEndOfDay() {
    return this.entity.activeFollowUp?.dueDate + ' 23:59:59';
  }

  get iconClasses() {
    if (this.isSubmitting) {
      return 'fa-spinner-third fa-spin';
    }
    return 'fa-flag';
  }

  get flagStyles() {
    if (this.isSubmitting) {
      return { background: '#4A5061', color: '#FFFFFF' }; // gray-05
    }

    if (this.isFlagOverdue()) {
      return { background: '#EF233C', color: '#FFFFFF'};
    } else if (this.isFlagged()) {
      return { background: '#F89E1C', color: '#FFFFFF' }; // orange-border
    }

    return { background: '#4A5061', color: '#FFFFFF' }; // gray-05
  }

  /**
   *
   *
   */
  constructor(
    private _completeFollowUpsGQL: CompleteActiveFollowUpsGQL,
    private _getEntityGQL: GetEntityById2GQL,
    private _toastrService: ToastrService,
    private _cd: ChangeDetectorRef,
    private _dialog: MatDialog
  ) {
    super();
  }

  ngOnInit(): void {
    this.watchForChangeKeys('entityId', 'campaignId').pipe(
      startWith<any, any>(null),
      takeUntil(this.disposer),
      map(() => ({ campaignId: this.campaignId, entityId: this.entityId })),
      filter(({ campaignId, entityId }) => !!campaignId && !!entityId),
      switchMap(({ campaignId, entityId }) => this._getEntityGQL.watch({
        entityId, campaignId,
        withToplines: false,
        withHighligtedTopline: false,
        withEntityType: false,
        withFollowUpDetails: true
      }, {
        fetchPolicy: 'cache-first'
      }).valueChanges),
      takeUntil(this.disposer),
      map(({ data }) => data?.getEntity)
    ).subscribe((entity) => {
      this.entity = entity;
      this._cd.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
  }

  isFlagged() {
    return this.entity.followUp;
  }

  isFlagOverdue() {
    return this.isFlagged() && this.entity.activeFollowUp?.dueDate
      ? isPast(parse(this.dueDateEndOfDay, 'yyyy-MM-dd HH:mm:ss', 0))
      : false;
  }

  openFollowUpFlagDialog() {
    FollowUpFlagDialog.open(this._dialog, {
      data: {
        campaignId: this.campaignId,
        entity: this.entity,
      }
    });
  }

  openFollowUpTableDialog(event?: MouseEvent) {
    event?.stopImmediatePropagation();
    FollowUpTableDialog.open(this._dialog, {
      data: {
        entityId: this.entityId,
        campaignId: this.campaignId
      }
    });
  }

  completeFollowUpFlag() {
    ConfirmDialogComponent.open(this._dialog, {
      data: {
        message: `Marking this follow up as complete will remove the flag.`
      }
    })
      .afterClosed()
      .pipe(
        switchMap(proceed => {
          if (!proceed) {
            return of(this.entity);
          }
          return this._completeFollowUpsGQL.mutate({
            input: {
              entityId: this.entity.id,
              campaignId: this.entity.campaignContextId
            }
          }).pipe(
            map(({ data }) => data.completeActiveFollowUps.entity),
            tap(() => this._toastrService.success('Follow Up Flag Completed'))
          );
        })
      )
      .subscribe((d) => {
        this.entity = d;
      });
  }
}
